/**
 * @Description:  全局跳转
 * @Author:  NvDsper
 * @Date: 2026-02-23 11:23:42
 */

export enum EGotoType {
  login = 'login',
  personalOpenAccount = 'personalOpenAccount',
  personalStartAccount = 'personalStartAccount',
  accountActivation = 'accountActivation',
  userInformation = 'userInformation',
}
type TGotoOptions = Partial<{
  jumpType: 'push' | 'replace'
  newTab: boolean
  query: Record<string, string>
  params: Record<string, string>
  router: any
  handleUrl: (url: string) => string
}>
type TEnv = 'sit' | 'uat' | 'prod'

const BASE_URL = {
  SIT: 'https://sit.mfosunhani.com',
  UAT: 'https://uat.mfosunhani.com',
  PROD: 'https://h5.mfosunhani.com',
}
const DEFAULT_CONFIG: Record<EGotoType, Record<TEnv, string>> = {
  [EGotoType.login]: {
    sit: BASE_URL.SIT + '/pages/login.html',
    uat: BASE_URL.UAT + '/pages/login.html',
    prod: BASE_URL.PROD + '/pages/login.html',
  },
  [EGotoType.personalOpenAccount]: {
    sit: BASE_URL.SIT + '/pages/openAccount.html',
    uat: BASE_URL.UAT + '/pages/openAccount.html',
    prod: BASE_URL.PROD + '/pages/openAccount.html',
  },
  [EGotoType.personalStartAccount]: {
    sit: BASE_URL.SIT + '/pages/startAccount.html',
    uat: BASE_URL.UAT + '/pages/startAccount.html',
    prod: BASE_URL.PROD + '/pages/startAccount.html',
  },
  [EGotoType.accountActivation]: {
    sit: BASE_URL.SIT + '/pages/accountActivation.html',
    uat: BASE_URL.UAT + '/pages/accountActivation.html',
    prod: BASE_URL.PROD + '/pages/accountActivation.html',
  },
  [EGotoType.userInformation]: {
    sit: BASE_URL.SIT + '/pages/userInformation.html',
    uat: BASE_URL.UAT + '/pages/userInformation.html',
    prod: BASE_URL.PROD + '/pages/userInformation.html',
  },
}

export default function goto(url: string | EGotoType, options?: TGotoOptions) {
  try {
    if (URL.canParse(url)) {
      handleUrl(url, options)
    } else if (Object.values(EGotoType).includes(url as EGotoType)) {
      handleCommonScene(url as EGotoType, options)
    } else {
      handleRoute(url, options)
    }
  } catch (error) {
    console.error(`[goto] ${url} 跳转失败`, error)
  }
}

function handleUrl(url: string, options?: TGotoOptions) {
  window.open(url, options?.newTab ? '_blank' : '_self')
}

function handleCommonScene(url: EGotoType, options?: TGotoOptions) {
  if (!DEFAULT_CONFIG[url]) {
    console.error(`[goto] ${url} 不存在`)
    return
  }
  let goUrl: string | undefined
  if (import.meta.env.VUE_APP_ENV) {
    goUrl = DEFAULT_CONFIG[url]?.[import.meta.env.VUE_APP_ENV as TEnv]
  } else {
    goUrl = DEFAULT_CONFIG[url]?.['prod']
  }
  let newUrl = new URL(goUrl)
  if (options?.query) {
    Object.entries(options.query).forEach(([key, value]) => {
      newUrl.searchParams.set(key, value)
    })
  }
  if (options?.handleUrl) {
    const url = options.handleUrl(newUrl.toString())
    newUrl = new URL(url)
  }
  handleUrl(newUrl.toString(), options)
}

function handleRoute(url: string, options?: TGotoOptions) {
  const router = options?.router
  if (!router) {
    console.error('[goto] router is not defined')
    return
  }
  if (options?.jumpType === 'replace') {
    router.replace({
      path: url,
      query: options.query,
      params: options.params,
    })
    return
  }
  router.push({
    path: url,
    query: options?.query ?? {},
    params: options?.params ?? {},
  })
}

/**
 * 将当前 URL 中的参数提取成一个对象
 * @param {string} url - 可选，指定要解析的 URL，默认使用 window.location.href
 * @returns {Object} 返回包含所有查询参数的对象
 * @example
 * // 当前 URL: https://example.com/page?id=123&name=test#/sub?a=1&b=2
 * getUrlParams() // 返回 {id: '123', name: 'test', a: '1', b: '2'}
 * getUrlParams('https://example.com/?x=1&y=2') // 返回 {x: '1', y: '2'}
 */
export function getUrlParams(url = window.location.href) {
  const params: Record<string, string> = {}

  try {
    const urlObj = new URL(url, window.location.origin)

    // 1. 提取普通查询参数（? 后面的部分）
    const searchParams = new URLSearchParams(urlObj.search)
    for (const [key, value] of searchParams.entries()) {
      params[key] = value
    }

    // 2. 提取 hash 中的查询参数（#/?a=1&b=2 这种形式）
    if (urlObj.hash && urlObj.hash.includes('?')) {
      const hashQuery = urlObj.hash.split('?')[1]
      if (hashQuery) {
        const hashParams = new URLSearchParams(hashQuery)
        for (const [key, value] of hashParams.entries()) {
          params[key] = value
        }
      }
    }
  } catch (error) {
    console.warn('URL 参数解析失败:', error)
  }

  return params
}
